import { InvalidSetupError } from "./setup";

/**
 * Party mode splits an online lobby of up to 40 players across up to 5 parallel
 * instances (tables) of at most 8 seats each. See docs/PARTY_MODE.md.
 *
 * The partition is mirrored in services/game-server/internal/engine, and the Go
 * copy is the authoritative one: it is what runs at deal time and decides who
 * actually sits where. This copy exists so the lobby can PREVIEW the split
 * before the deal, from the live roster, without a round trip. Same numbers,
 * mirrored tests — keep the two in step per the two-engines rule in CLAUDE.md.
 */

/** A table still can't seat more than 8; party mode adds tables, not seats. */
export const MAX_PLAYERS_PER_INSTANCE = 8;
export const MAX_PARTY_INSTANCES = 5;
/** 8 seats x 5 instances. */
export const MAX_PARTY_PLAYERS = 40;
/**
 * The fewest players an ONLINE deal will seat: one is enough, whether that is a
 * host alone or a guest whose host is watching. The server refuses a deal below
 * it. Mirrors `MinOnlinePlayers` in the Go engine.
 */
export const MIN_ONLINE_PLAYERS = 1;
/**
 * The table an online host's setup screens assume, however few have joined yet:
 * somebody may still sit down, so the multiplayer rules stay on offer.
 */
export const ONLINE_SETUP_PLAYERS = 2;

/**
 * How a lobby of `playerCount` splits into instances, largest first.
 *
 * Sizes are balanced rather than packed (9 is [5,4], not [8,1]) so no instance
 * plays a lopsided round. A solo table is no longer a problem for the server
 * (it finishes a solo party table `round-over` like any other), but the AUTO
 * split still never makes one: 9 or more players means at least 4 per table.
 * A host who wants a solo table asks for it with a custom layout (`tableGroups`).
 *
 * A count of 8 or fewer returns a single entry; callers read length 1 as "not a
 * party" and take the unchanged legacy single-table path.
 */
export function partyInstanceSizes(playerCount: number): number[] {
  if (!Number.isInteger(playerCount)) {
    throw new InvalidSetupError("player count must be a whole number");
  }
  if (playerCount < 1 || playerCount > MAX_PARTY_PLAYERS) {
    throw new InvalidSetupError(`a party seats 1-${MAX_PARTY_PLAYERS} players`);
  }
  if (playerCount <= MAX_PLAYERS_PER_INSTANCE) return [playerCount];

  const n = Math.ceil(playerCount / MAX_PLAYERS_PER_INSTANCE);
  const base = Math.floor(playerCount / n);
  const remainder = playerCount % n;
  return Array.from({ length: n }, (_, i) => (i < remainder ? base + 1 : base));
}

/**
 * Splits a roster into its instance groups: contiguous slices in seat order.
 *
 * Contiguous means shuffling the party re-groups it by permuting the roster
 * itself (POST /games/{id}/shuffle), leaving the partition deterministic.
 */
export function partyGroups<T>(players: T[]): T[][] {
  const sizes = partyInstanceSizes(players.length);
  const groups: T[][] = [];
  let start = 0;
  for (const size of sizes) {
    groups.push(players.slice(start, start + size));
    start += size;
  }
  return groups;
}

/**
 * Seats at the biggest table this lobby would produce — what deck config has to
 * be validated against, since the deck is dealt per instance, not per party.
 * Equal to `playerCount` when the lobby fits on one table.
 */
export function maxInstancePlayerCount(playerCount: number): number {
  return partyInstanceSizes(playerCount)[0];
}

/** The fewest players any table holds is none: a custom layout may leave one empty. */
export interface TableLayout {
  /** The table count after normalizing; can exceed the one passed in (see `normalizeTables`). */
  tableCount: number;
  /** Player id -> 1-based table number. Empty in auto mode (`tableCount` 0). */
  tableOf: Map<string, number>;
}

/**
 * Where each player sits in a host-arranged (CUSTOM) layout, the lobby's
 * mirror of the Go server's normalization. A player the host never placed, or
 * whose table was removed, has no valid `table`; the server puts them at the
 * table with the fewest players (ties to the lowest number), opening a new
 * table when that one is already full and there is room for one. Running the
 * same rule here is what lets the preview draw them where the deal will.
 *
 * `players` is the next-deal roster order (seated, then pending): the walk is in
 * that order, and players who already hold a valid table count toward the sizes
 * from the start, so an unplaced player never lands on top of a full table the
 * walk simply hadn't reached yet.
 *
 * `tableCount` 0 is AUTO mode and returns an empty map: tables are then derived
 * from the roster (`partyGroups`), not assigned.
 */
export function normalizeTables(players: { id: string; table?: number }[], tableCount: number): TableLayout {
  if (!(tableCount > 0)) return { tableCount: 0, tableOf: new Map() };
  let count = tableCount;
  const tableOf = new Map<string, number>();
  const sizes = new Array<number>(MAX_PARTY_INSTANCES + 1).fill(0);
  const valid = (t: number | undefined): t is number => !!t && Number.isInteger(t) && t >= 1 && t <= tableCount;
  for (const p of players) {
    if (valid(p.table)) {
      tableOf.set(p.id, p.table);
      sizes[p.table]++;
    }
  }
  for (const p of players) {
    if (tableOf.has(p.id)) continue;
    let best = 1;
    for (let t = 2; t <= count; t++) if (sizes[t] < sizes[best]) best = t;
    if (sizes[best] >= MAX_PLAYERS_PER_INSTANCE && count < MAX_PARTY_INSTANCES) {
      count++;
      best = count;
    }
    tableOf.set(p.id, best);
    sizes[best]++;
  }
  return { tableCount: count, tableOf };
}

/**
 * The lobby's tables for a roster, honouring a host-arranged layout.
 *
 * `tableCount` 0 is `partyGroups(players)`, unchanged. Otherwise one group per
 * table 1..N in roster order, EMPTY TABLES INCLUDED as `[]` so the lobby can
 * draw them and accept a drop on them. The server does not deal an empty table;
 * callers that count tables to be played must drop the empty ones themselves.
 */
export function tableGroups<T extends { id: string; table?: number }>(players: T[], tableCount: number): T[][] {
  if (!(tableCount > 0)) return partyGroups(players);
  const layout = normalizeTables(players, tableCount);
  const groups: T[][] = Array.from({ length: layout.tableCount }, () => []);
  for (const p of players) groups[layout.tableOf.get(p.id)! - 1]!.push(p);
  return groups;
}
